import React, { useId } from "react";
import { cn } from "../../lib/utils";

/**
 * The monogram.
 *
 * A volt tile with a "c" and a caret cut out of it. The letter leans by the
 * same angle as the highlighter and the buttons — that lean is the identity,
 * and the mark is where it should be most obvious.
 *
 * The caret is what stops this from being a lonely initial: it sits in the
 * letter's opening, reads as a cursor or a pixel depending on your mood, and
 * gives the mark an asymmetry you can still pick out at 16px.
 *
 * Two decisions worth keeping:
 *
 *   - **Geometry, never <text>.** A mark built from a webfont draws as Arial
 *     for the first few hundred milliseconds of a cold load, and the logo is
 *     the one thing that may never look wrong.
 *   - **The tile is full bleed, the letter leans.** Skewing the tile instead
 *     eats its corners, which at small sizes is most of the mark. This shape
 *     is identical to public/icon.svg, so the favicon and the header are the
 *     same drawing rather than two things that resemble each other.
 *
 * Cut out rather than drawn on, so the page shows through the letter.
 */
export default function Mark({ size = 34, className, decorative = false, title = "Casper" }) {
  /* A document-unique id, or two marks on one page share a mask and the
     second renders as a solid tile. */
  const id = useId().replace(/:/g, "");

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      className={cn("shrink-0", className)}
      role={decorative ? "presentation" : "img"}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : title}
    >
      <defs>
        <mask id={`m-${id}`}>
          <rect width="48" height="48" rx="11" fill="#fff" />
          <g transform="translate(24 24) skewX(-6) translate(-24 -24)">
            {/* The c: a stroked circle with a wedge removed. Circumference is
                2·pi·9.5 ≈ 59.7, so 40 on / 20 off leaves a 120° opening, and
                the rotation swings that opening around to face right. */}
            <circle
              cx="23"
              cy="24"
              r="9.5"
              fill="none"
              stroke="#000"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray="40 20"
              transform="rotate(59 23 24)"
            />
            <rect x="30" y="20.8" width="6.4" height="6.4" rx="1.5" fill="#000" />
          </g>
        </mask>
      </defs>

      <rect width="48" height="48" rx="11" fill="var(--c-accent)" mask={`url(#m-${id})`} />
    </svg>
  );
}

/** The mark plus the wordmark. */
export function Lockup({ size = 26, className, showName = true }) {
  return (
    <span className={cn("inline-flex items-center gap-2.5", className)}>
      <Mark size={size} decorative />
      {showName && (
        <span
          className="font-display font-bold lowercase"
          style={{ fontSize: size * 0.62, letterSpacing: "-0.06em" }}
        >
          casper
        </span>
      )}
    </span>
  );
}
